import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import CustomDropdown from './CustomDropdown';

const QuickQuoteForm = () => {
  const [formData, setFormData] = useState({
    buildingTypes: [],
    height: '',
    width: '',
    length: '',
    timeline: '',
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    projectLocation: '',
    files: []
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const fileInputRef = useRef(null);

  const buildingTypeOptions = [
    { id: 'commercial', name: 'Commercial', icon: '🏢' },
    { id: 'residential', name: 'Residential', icon: '🏠' },
    { id: 'warehouse', name: 'Warehouse', icon: '🏭' },
    { id: 'workshop', name: 'Workshop', icon: '🔧' },
    { id: 'garage', name: 'Garage', icon: '🚗' },
    { id: 'agricultural', name: 'Agricultural', icon: '🚜' },
    { id: 'storage', name: 'Storage', icon: '📦' },
    { id: 'custom', name: 'Custom', icon: '⚙️' }
  ];

  const timelineOptions = [
    { value: 'immediate', label: 'Immediate (1-2 months)' },
    { value: '3-6-months', label: '3-6 months' },
    { value: '6-12-months', label: '6-12 months' },
    { value: '12-plus-months', label: '12+ months' },
    { value: 'planning', label: 'Just planning' }
  ];

  const provinceOptions = [
    { value: '', label: 'Select Province' },
    { value: 'AB', label: 'Alberta' },
    { value: 'BC', label: 'British Columbia' },
    { value: 'MB', label: 'Manitoba' },
    { value: 'NB', label: 'New Brunswick' },
    { value: 'NL', label: 'Newfoundland and Labrador' },
    { value: 'NS', label: 'Nova Scotia' },
    { value: 'NT', label: 'Northwest Territories' },
    { value: 'NU', label: 'Nunavut' },
    { value: 'ON', label: 'Ontario' },
    { value: 'PE', label: 'Prince Edward Island' },
    { value: 'QC', label: 'Quebec' },
    { value: 'SK', label: 'Saskatchewan' },
    { value: 'YT', label: 'Yukon' }
  ];

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const re = /^[\+]?[1-9][\d]{0,15}$/;
    return re.test(phone.replace(/[\s\-\(\)]/g, ''));
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (formData.buildingTypes.length === 0) {
      newErrors.buildingTypes = 'Please select at least one building type';
    }

    // Height, width, length, and timeline are now optional
    // Only validate if they have values
    if (formData.height && formData.height < 8) {
      newErrors.height = 'Height must be at least 8 feet';
    }

    if (formData.width && formData.width < 10) {
      newErrors.width = 'Width must be at least 10 feet';
    }

    if (formData.length && formData.length < 10) {
      newErrors.length = 'Length must be at least 10 feet';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.projectLocation.trim()) {
      newErrors.projectLocation = 'Project location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBuildingTypeToggle = (typeId) => {
    setFormData(prev => ({
      ...prev,
      buildingTypes: prev.buildingTypes.includes(typeId)
        ? prev.buildingTypes.filter(id => id !== typeId)
        : [...prev.buildingTypes, typeId]
    }));
    if (errors.buildingTypes) {
      setErrors(prev => ({ ...prev, buildingTypes: '' }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileUpload = (files) => {
    const validFiles = Array.from(files).filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
      return isValidType && isValidSize;
    });

    if (formData.files.length + validFiles.length > 5) {
      alert('Maximum 5 files allowed');
      return;
    }

    setFormData(prev => ({
      ...prev,
      files: [...prev.files, ...validFiles]
    }));
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const nextStep = () => {
    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  const prevStep = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData for Netlify Forms
      const submitData = new FormData();
      
      // Add form name (must match the name attribute in the hidden form)
      submitData.append('form-name', 'quote-request');
      
      // Add all form fields
      submitData.append('buildingTypes', JSON.stringify(formData.buildingTypes));
      submitData.append('height', formData.height || '');
      submitData.append('width', formData.width || '');
      submitData.append('length', formData.length || '');
      submitData.append('timeline', formData.timeline || '');
      submitData.append('fullName', formData.fullName);
      submitData.append('companyName', formData.companyName || '');
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('projectLocation', formData.projectLocation);
      submitData.append('budget', formData.budget || '');
      
      // Add files
      formData.files.forEach(file => {
        submitData.append('files', file);
      });

      // Submit to Netlify Forms
      // Netlify automatically processes forms with form-name matching a form with data-netlify="true"
      const response = await fetch('/', {
        method: 'POST',
        body: submitData
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert(`There was an error submitting your quote request: ${error.message}. Please try again or contact us directly.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
              <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full h-full flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            className="card max-w-md w-full text-center"
          >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </motion.div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Quote Request Submitted!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for your interest! Our team will review your project details and get back to you within 24 hours with a personalized quote.
          </p>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setIsSubmitted(false);
              setCurrentStep(1);
              setFormData({
                buildingTypes: [],
                height: '',
                width: '',
                length: '',
                timeline: '',
                fullName: '',
                companyName: '',
                email: '',
                phone: '',
                projectLocation: '',
                files: []
              });
              setErrors({});
            }}
            className="btn-primary"
          >
            Submit Another Quote
          </motion.button>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <div className="form-container w-full flex items-center justify-center py-4" style={{ minHeight: '500px' }}>
      {/* Step 1: Horizontal Bar */}
      {currentStep === 1 && (
                        <motion.form
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  onSubmit={e => { e.preventDefault(); nextStep(); }}
                  className="form-step w-full max-w-2xl bg-white/90 shadow-xl rounded-2xl px-4 md:px-8 py-4 md:py-6 flex flex-col items-center"
                  style={{ zIndex: 10, minHeight: '460px' }}
                >
                  {/* Heading */}
                  <div className="w-full text-center mb-4">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-800">Receive a Steel Building Estimate</h2>
                  </div>
                  
                  {/* Step Indicator */}
                  <div className="w-full text-left mb-4">
                    <span className="text-sm font-semibold text-gray-700">Step 1 of 2</span>
                  </div>
          {/* Fields Grid */}
          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                {/* Building Type */}
                    <div className="col-span-1">
                      <CustomDropdown
                        value={formData.buildingTypes[0] || ''}
                        onChange={handleBuildingTypeToggle}
                        options={buildingTypeOptions}
                        placeholder="Building Type"
                        icon={<svg xmlns='http://www.w3.org/2000/svg' className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' /></svg>}
                        error={!!errors.buildingTypes}
                      />
                    </div>
                                {/* Province */}
                    <div className="col-span-1">
                      <CustomDropdown
                        value={formData.projectLocation}
                        onChange={(value) => handleInputChange('projectLocation', value)}
                        options={provinceOptions}
                        placeholder="Province"
                        icon={<svg xmlns='http://www.w3.org/2000/svg' className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z' /><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 11a3 3 0 11-6 0 3 3 0 016 0z' /></svg>}
                      />
                    </div>
          </div>
          {/* Dimensions Grid */}
          <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            {/* Height */}
            <div className="col-span-1">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-600">
                  <svg xmlns='http://www.w3.org/2000/svg' className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 11l5-5m0 0l5 5m-5-5v12' /></svg>
                </span>
                <input
                  type="number"
                  min="8"
                  value={formData.height}
                  onChange={e => handleInputChange('height', e.target.value)}
                  className={`form-input pl-10 !py-3 !rounded-xl text-base md:text-lg font-medium bg-white border border-gray-200 shadow-sm focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all duration-150 w-full h-12 md:h-14 ${errors.height ? 'error' : ''}`}
                  placeholder="Height (ft)"
                />
              </div>
            </div>
            {/* Width */}
            <div className="col-span-1">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  <svg xmlns='http://www.w3.org/2000/svg' className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 6h16M4 10h16M4 14h16M4 18h16' /></svg>
                </span>
                <input
                  type="number"
                  min="10"
                  value={formData.width}
                  onChange={e => handleInputChange('width', e.target.value)}
                  className={`form-input pl-10 !py-3 !rounded-xl text-base md:text-lg font-medium bg-white border border-gray-200 shadow-sm focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all duration-150 w-full h-12 md:h-14 ${errors.width ? 'error' : ''}`}
                  placeholder="Width (ft)"
                />
              </div>
            </div>
            {/* Length */}
            <div className="col-span-1">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  <svg xmlns='http://www.w3.org/2000/svg' className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 6h16M4 10h16M4 14h16M4 18h16' /></svg>
                </span>
                <input
                  type="number"
                  min="10"
                  value={formData.length}
                  onChange={e => handleInputChange('length', e.target.value)}
                  className={`form-input pl-10 !py-3 !rounded-xl text-base md:text-lg font-medium bg-white border border-gray-200 shadow-sm focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all duration-150 w-full h-12 md:h-14 ${errors.length ? 'error' : ''}`}
                  placeholder="Length (ft)"
                />
              </div>
            </div>
          </div>
                            {/* Timeline (full width) */}
                  <div className="w-full mb-4">
                    <CustomDropdown
                      value={formData.timeline}
                      onChange={(value) => handleInputChange('timeline', value)}
                      options={timelineOptions}
                      placeholder="Timeline"
                      icon={<svg xmlns='http://www.w3.org/2000/svg' className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' /></svg>}
                      error={!!errors.timeline}
                    />
                  </div>
          {/* CTA Button */}
          <button
            type="submit"
            className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-base md:text-lg rounded-xl py-3 md:py-4 shadow-lg transition-all duration-150 mb-2 flex items-center justify-center"
          >
            Get My Instant Estimate
          </button>
          {/* Info Row */}
          <div className="w-full flex items-center justify-center mt-1 text-xs text-gray-500">
            Receive a fast quote and expert help within 24 hours.
          </div>
        </motion.form>
      )}

      {/* Step 2: Modal Overlay */}
      {currentStep === 2 && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30">
                          <motion.form
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                  onSubmit={handleSubmit}
                  className="form-step w-full max-w-2xl bg-white/90 shadow-xl rounded-2xl px-4 md:px-8 py-4 md:py-6 flex flex-col items-center"
                  style={{ minHeight: '460px' }}
                >
                  {/* Heading */}
                  <div className="w-full text-center mb-4">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-800">Receive a Steel Building Estimate</h2>
                  </div>
                  
                  {/* Step Indicator */}
                  <div className="w-full text-left mb-4">
                    <span className="text-sm font-semibold text-gray-700">Step 2 of 2</span>
                  </div>
            {/* Fields Grid */}
            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              {/* Full Name */}
              <div className="col-span-1">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-700">
                    <svg xmlns='http://www.w3.org/2000/svg' className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5.121 17.804A13.937 13.937 0 0112 15c2.5 0 4.847.655 6.879 1.804M15 11a3 3 0 11-6 0 3 3 0 016 0z' /></svg>
                  </span>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={e => handleInputChange('fullName', e.target.value)}
                    className={`form-input pl-10 !py-3 !rounded-xl text-base md:text-lg font-medium bg-white border border-gray-200 shadow-sm focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all duration-150 w-full h-12 md:h-14 ${errors.fullName ? 'error' : ''}`}
                    placeholder="Full Name*"
                  />
                </div>
              </div>
              {/* Company */}
              <div className="col-span-1">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    <svg xmlns='http://www.w3.org/2000/svg' className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' /></svg>
                  </span>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={e => handleInputChange('companyName', e.target.value)}
                    className="form-input pl-10 !py-3 !rounded-xl text-base md:text-lg font-medium bg-white border border-gray-200 shadow-sm focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all duration-150 w-full h-12 md:h-14"
                    placeholder="Company (optional)"
                  />
                </div>
              </div>
              {/* Email */}
              <div className="col-span-1">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-600">
                    <svg xmlns='http://www.w3.org/2000/svg' className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' /></svg>
                  </span>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => handleInputChange('email', e.target.value)}
                    className={`form-input pl-10 !py-3 !rounded-xl text-base md:text-lg font-medium bg-white border border-gray-200 shadow-sm focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all duration-150 w-full h-12 md:h-14 ${errors.email ? 'error' : ''}`}
                    placeholder="Email*"
                  />
                </div>
              </div>
              {/* Phone */}
              <div className="col-span-1">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500">
                    <svg xmlns='http://www.w3.org/2000/svg' className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z' /></svg>
                  </span>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={e => handleInputChange('phone', e.target.value)}
                    className={`form-input pl-10 !py-3 !rounded-xl text-base md:text-lg font-medium bg-white border border-gray-200 shadow-sm focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all duration-150 w-full h-12 md:h-14 ${errors.phone ? 'error' : ''}`}
                    placeholder="Phone*"
                  />
                </div>
              </div>
              {/* SMS Consent Disclaimer */}
              <div className="col-span-1 md:col-span-2 mt-1 mb-2">
                <p className="text-[10px] md:text-xs text-gray-500 text-center leading-tight px-2">
                  By submitting, you agree to receive recurring SMS messages from Made in Canada Steel Buildings about your quote and project updates. Msg & data rates may apply. Reply STOP to unsubscribe.
                </p>
              </div>
                                  {/* Budget Dropdown */}
                    <div className="col-span-1">
                      <CustomDropdown
                        value={formData.budget || ''}
                        onChange={(value) => handleInputChange('budget', value)}
                        options={[
                          { value: "0-100k", label: "$0k–$100k" },
                          { value: "100-200k", label: "$100k–$200k" },
                          { value: "200-400k", label: "$200k–$400k" },
                          { value: "400-600k", label: "$400k–$600k" },
                          { value: "600k-1m", label: "$600k–$1 million" },
                          { value: "1m+", label: "$1 million+" }
                        ]}
                        placeholder="Budget"
                        icon={<svg xmlns='http://www.w3.org/2000/svg' className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z' /></svg>}
                      />
                    </div>
              {/* File Upload */}
              <div className="col-span-1 flex items-center">
                <div className="relative w-full">
                  <label className="sr-only">Project Files</label>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="form-input w-full h-12 pl-10 flex items-center justify-start border border-dashed border-gray-300 rounded-xl bg-white hover:bg-gray-50 transition-all duration-150 cursor-pointer text-left"
                  >
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      <svg xmlns='http://www.w3.org/2000/svg' className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15.172 7l-6.586 6.586a2 2 0 102.828 2.828L18 9.828M7 17h.01' /></svg>
                    </span>
                    <span className="ml-6 text-gray-500 text-base">Upload</span>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    onChange={e => handleFileUpload(e.target.files)}
                    className="hidden"
                  />
                </div>
              </div>
            </div>
            {/* CTA Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-base md:text-lg rounded-xl py-3 md:py-4 shadow-lg transition-all duration-150 mb-2 flex items-center justify-center"
            >
              <span className="mr-2">{isSubmitting ? 'Submitting...' : 'Submit & Get My Quote'}</span>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16.5V19a2 2 0 002 2h12a2 2 0 002-2v-2.5M12 3v13m0 0l-4-4m4 4l4-4" />
              </svg>
            </button>
            {/* Info Row */}
            <div className="w-full flex items-center justify-center mt-1 text-xs text-gray-500">
              Receive a fast quote and expert help within 24 hours.
            </div>
          </motion.form>
        </div>
      )}

      {/* Success State (unchanged) */}
      {isSubmitted && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/30"
        >
          <motion.div
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            className="card max-w-md w-full text-center bg-white/90 backdrop-blur border border-gray-200 shadow-2xl rounded-2xl p-8"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </motion.div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Quote Request Submitted!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for your interest! Our team will review your project details and get back to you within 24 hours with a personalized quote.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setIsSubmitted(false);
                setCurrentStep(1);
                setFormData({
                  buildingTypes: [],
                  width: '',
                  length: '',
                  timeline: '',
                  fullName: '',
                  companyName: '',
                  email: '',
                  phone: '',
                  projectLocation: '',
                  files: []
                });
                setErrors({});
              }}
              className="btn-primary"
            >
              Submit Another Quote
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default QuickQuoteForm;